import { inject, Injectable, NgZone, signal } from '@angular/core';

import { MdwEmoji } from '../models';

import { EmojiMartAdapter } from './emoji-mart-adapter';

/**
 * Reads emoji-mart's globally-selected skin tone as a colons suffix (`''` for the
 * default tone).
 *
 * Guarded because the result is consumed from `computed()`s during change detection,
 * and `getItem` itself throws — rather than returning null — when storage access is
 * blocked (private-mode Safari, some embedded webviews).
 */
function readSkinToneSuffix(): string {
  try {
    const skinTone = localStorage.getItem('emoji-mart.skin') ?? '1';

    return skinTone === '1' ? '' : `:skin-tone-${skinTone}:`;
  } catch {
    return '';
  }
}

@Injectable({ providedIn: 'root' })
export class MdwEmojiSearchService {
  private adapter = inject(EmojiMartAdapter);
  private zone = inject(NgZone);

  private readonly skinToneSuffix = signal(readSkinToneSuffix());
  private skinToneRefreshQueued = false;

  constructor() {
    // `storage` fires only in *other* windows/tabs, which is exactly the gap the
    // picker-driven refresh can't cover: a tone changed in the main window would
    // otherwise stay stale in a popped-out call window until its own picker was used.
    window.addEventListener('storage', event => {
      if (event.key === null || event.key === 'emoji-mart.skin') {
        this.refreshSkinTone();
      }
    });
  }

  public async search(query: string): Promise<MdwEmoji[]> {
    // emoji-mart's SearchIndex matches emoticons (e.g. `:)`) only when the
    // leading `:` is present, but TipTap's Suggestion strips the trigger char
    // before handing us the query. Run both searches so emoticon results
    // surface when we're called with the bare tail (e.g. `)` or `D`).
    const [rawResults, emoticonResults] = query.startsWith(':')
      ? [await this.adapter.search(query), [] as MdwEmoji[]]
      : await Promise.all([
          this.adapter.search(query),
          this.adapter.search(`:${query}`),
        ]);

    if (!rawResults.length && !emoticonResults.length) {
      return [];
    }

    const words = query.split(' ');
    const filteredRaw = rawResults.filter(emoji => {
      const emojiName = emoji.name.toLowerCase();
      const emojiColons = emoji.colons?.toLowerCase() ?? '';

      return words.every(word => {
        const lowerWord = word.toLowerCase();
        return (
          emojiName.includes(lowerWord) ||
          emojiColons.includes(lowerWord)
        );
      });
    });

    // Fall back to raw results for queries that bypass the name/colons filter,
    // e.g. emoticons like :) which match via emoji-mart's emoticon index.
    const rawSection = filteredRaw.length ? filteredRaw : rawResults;

    if (!emoticonResults.length) {
      return rawSection;
    }

    // Always prepend emoticon matches (e.g. typing `:D` should surface
    // `grinning` before `dagger_knife`, which the substring filter alone would
    // otherwise rank by name match).
    const seen = new Set<string>();
    const merged: MdwEmoji[] = [];

    for (const emoji of [...emoticonResults, ...rawSection]) {
      if (seen.has(emoji.id)) {
        continue;
      }

      seen.add(emoji.id);
      merged.push(emoji);
    }

    return merged;
  }

  public getEmojiByName(colonsName: string) {
    return this.adapter.getEmojiByColons(colonsName);
  }

  /**
   * The user's globally-selected skin tone as a colons suffix (`''` for the default).
   *
   * Signal-backed so `computed()`s that resolve tone-aware emoji recompute when the
   * tone changes, and so the value is read from storage once per change instead of
   * once per emoji resolved.
   */
  public getEmojiSkinTone() {
    return this.skinToneSuffix();
  }

  /**
   * Re-reads the stored skin tone.
   *
   * emoji-mart owns the `emoji-mart.skin` key and writes it from inside the picker's
   * shadow DOM (`handleSkinClick`) with no change callback to subscribe to, so
   * {@link MdwEmojiPanel} calls this on picker interaction rather than us reaching
   * into emoji-mart's internals.
   */
  public refreshSkinTone() {
    this.skinToneSuffix.set(readSkinToneSuffix());
  }

  /**
   * Queues a {@link refreshSkinTone}, coalescing repeat calls into one microtask.
   *
   * Used for the high-frequency callers in {@link MdwEmojiPanel} (its picker
   * MutationObserver fires on every preview hover, plus capture-phase click/keydown
   * listeners), so a burst of notifications costs one guarded storage read rather
   * than one per notification.
   *
   * Those callers run outside the Angular zone — the panel creates the picker in
   * `runOutsideAngular` precisely so hovering and typing in it cost no change
   * detection. The zone is therefore entered only when the stored tone actually
   * differs from the cached one (i.e. once per real tone selection): the OnPush
   * hover bar must repaint on a change without relying on signal-driven scheduling,
   * but an unconditional `zone.run` here would turn every picker interaction into a
   * full app tick.
   *
   * Note the deferral is NOT by itself enough to observe a skin-tone change: driving
   * the refresh from capture-phase picker listeners left the tone one selection
   * behind in the real app even with the microtask. The panel's MutationObserver is
   * the mechanism that actually works — see the comment there.
   */
  public scheduleSkinToneRefresh() {
    if (this.skinToneRefreshQueued) {
      return;
    }

    this.skinToneRefreshQueued = true;
    queueMicrotask(() => {
      this.skinToneRefreshQueued = false;

      const next = readSkinToneSuffix();
      if (next === this.skinToneSuffix()) {
        return;
      }

      this.zone.run(() => this.skinToneSuffix.set(next));
    });
  }

  public getEmojiByValue(value: string) {
    return this.adapter.getEmojiByNative(value);
  }
}
